import * as React from 'react';
import colors from '../../../config/theme/colors';

import { field, historyValue } from '../../../config/theme/componentTheme';
import { styled } from '@mui/system';

const StyledDiv = styled('div')({
  width: '100%',
});

export const StyledTextArea = styled('textarea', {
  shouldForwardProp: prop => prop !== 'fullWidth' && prop !== 'maxHeight',
})<ITextAreaProps>(({ width, fullWidth, maxLength, maxHeight }: ITextAreaProps) => ({
  width: width ? `${width}rem` : fullWidth ? '100%' : '90%',
  maxlength: maxLength ? maxLength : undefined,
  borderRadius: field.borderRadius,
  fontSize: field.fontSize,
  fontWeight: field.fontWeight,
  textAlign: 'left',
  fontFamily: 'Titillium Web',
  letterSpacing: field.letterSpacing,
  boxShadow: field.boxShadow,
  border: field.border,
  padding: field.padding,
  boxSizing: 'border-box',
  maxHeight: maxHeight ? `${maxHeight}rem` : undefined,
}));

interface ITextAreaProps {
  width?: number | string;
  fullWidth?: boolean;
  maxLength?: number;
  maxHeight?: number;
}

/*
  React forgets the caret (or selection) position in TextArea elements when the value is controlled outside the component.
  React only handles positioning automatically if the onChange and setState are called within the same component.
  A workaround to this is to store the actual selection in each update and set it back to where it was after react has completed DOM manipulations
  using useLayoutEffect.
  Without this workaround, the caret will be moved to the end regardless of the position where the change was made. E.g. caret is moved to the end on each
  character entered in the middle of the text, making edits tedious to do without erasing the contents of the text area and rewriting it.
*/
const CaretControlledTextArea = ({
  name,
  id,
  value,
  onChange,
  placeholder,
  width,
  rows = 5,
  fullWidth,
  style,
  maxLength,
  autoHeight,
  maxHeight,
}: Partial<IInputBasics & ITextArea>): React.JSX.Element => {
  const [innerValue, setInnerValue] = React.useState<string>(
    value !== null && value !== undefined ? String(value) : '',
  );

  const debounceRef = React.useRef<number | null>(null);
  const inputRef = React.useRef<HTMLTextAreaElement>(null);

  const saveValueDebounced = (newValue: string): void => {
    if (!onChange || !name) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      onChange({ [name]: newValue });
      debounceRef.current = null;
    }, 500);
  };

  const flush = () => {
    if (!onChange || !name) return;
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    onChange({ [name]: innerValue });
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setInnerValue(e.target.value);
    saveValueDebounced(e.target.value);
  };

  const autoResize = React.useCallback((): void => {
    if (!autoHeight || !inputRef.current?.style) return;
    inputRef.current.style.height = 'auto';
    inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
  }, [autoHeight]);

  const savedCaret = React.useRef<[number, number] | null>(null);

  React.useEffect(() => {
    const nextValue = value !== null && value !== undefined ? String(value) : '';
    // If the incoming value differs from the value in state: update it and store caret for restore.
    // This should only be handled if the parent onChange-method modified the content in some way.
    // Note that this implementation, or the implementation before, is/was not really compatible with onChange modifications
    // due to the debounce. There will always be a 500ms delay in seeing those changes
    // Removing the caret handling will cause external changes to send caret to the end of the text.
    if (nextValue !== innerValue) {
      if (inputRef.current?.selectionEnd !== undefined) {
        savedCaret.current = [inputRef.current.selectionStart, inputRef.current.selectionEnd];
      }
      setInnerValue(nextValue);
    }
  }, [value]);

  React.useLayoutEffect(() => {
    if (!inputRef.current || !savedCaret.current) return;
    inputRef.current.setSelectionRange(savedCaret.current[0], savedCaret.current[1]);
    savedCaret.current = null;
  }, [innerValue]);

  return (
    <>
      <StyledTextArea
        ref={inputRef}
        id={id}
        name={name}
        rows={rows}
        placeholder={placeholder}
        onChange={handleChange}
        onInput={autoResize}
        onBlur={flush}
        onKeyDown={(e: { key: string }): void => {
          if (e.key === 'Enter') flush();
        }}
        value={innerValue}
        width={width}
        fullWidth={fullWidth}
        style={{
          color: style && style.color ? `${style.color}` : undefined,
          overflowY: autoHeight ? 'hidden' : 'unset',
        }}
        maxLength={maxLength}
        maxHeight={maxHeight}
      />
      {maxLength && (
        <div
          style={{
            width: fullWidth ? '100%' : width ? `${width}rem` : '90%',
            textAlign: 'end',
            color: colors.tertiaryText,
          }}
        >{`${innerValue.length ?? 0} / ${maxLength}`}</div>
      )}
    </>
  );
};

const TextArea = ({ editing = false, value, ...props }: IInputBasics & ITextArea): React.JSX.Element =>
  !editing ? (
    <StyledDiv
      style={{
        ...historyValue,
        whiteSpace: 'pre-line',
        color: props.style && props.style.color ? `${props.style.color}` : undefined,
      }}
    >
      {value || '-'}
    </StyledDiv>
  ) : (
    <CaretControlledTextArea value={value} editing={editing} {...props} />
  );

export default TextArea;
